import { AbstractSelection } from '../system/classes/selection';
import { uniqArray } from '../system/helpers';
import { typedFastCopy } from '../utils';

export class ArraySelection<T> extends AbstractSelection<T> {
    protected _arr: any[];
    //	protected _id: number;
    protected _size: number = 0;
    //	protected _idField = 'id';
    protected data: Array<T> = [];
    //protected FULL objects
    protected selectedData: Array<T> = [];

    constructor(arr: any[], idField = 'id') {
        super();
        this._arr = arr;
        this._idField = idField;
        this._size = this._arr?.length;
    }

    public get global() {
        return false;
    }

    public setData(data: Array<T>) {
        this.data = data;
    }

    public getSelectionData() {
        return this.selectedData;
    }

    public update() {
        return this.select([]);
    }

    public isSelected(item: T): boolean {
        return this._arr.includes(item[this._idField]);
    }

    public setSelection(item: T, value: boolean, stealth: boolean = false): void {
        if (value && !this.isSelected(item)) {
            this._arr.push(item[this._idField]);
            this.selectedData.push(item);
        } else if (!value && this.isSelected(item)) {
            const index = this._arr.indexOf(item[this._idField]);
            this._arr.splice(index, 1);
            this.selectedData = this.selectedData.filter(itemObj => item[this._idField] !== itemObj[this._idField]);
        }
        if (!stealth) {
            this.emit('select', item);
            this.emit('updated', this._arr);
        }
        this._updateState(this._id, this._arr.length);
    }

    public async getSelectedItems(): Promise<any[]> {
        return Promise.resolve(this._arr.map(item => item));
    }

    public getSelection(): any[] {
        return typedFastCopy(this._arr);
    }

    protected _modify(selectItems: T[], deselectItems: T[]): Promise<void> {
        selectItems.forEach(item => {
            this.setSelection(item, true);
        });

        deselectItems.forEach(item => {
            this.setSelection(item, false);
        });

        return Promise.resolve();
    }

    protected _selectAll(): Promise<void> {
        this._arr.splice(0, this._arr.length);
        this.data.forEach(item => {
            this._arr.push(item[this._idField]);
            this.selectedData.push(item);
        });
        this._updateState(this._id, this._arr.length);
        return Promise.resolve();
    }

    protected _deselectAll(): Promise<void> {
        return this._remove();
    }

    protected _invertAll(): Promise<void> {
        return Promise.resolve();
    }

    protected _remove(): Promise<void> {
        this._arr.splice(0, this._arr.length);
        this.selectedData.splice(0, this.selectedData.length);
        this._updateState(this._id, this._arr.length);
        return Promise.resolve();
    }

    public invert(items: T[]): Promise<void> {
        const itemsUniq = uniqArray<T>(items, this._idField);
        const toBeSelected = itemsUniq.filter(item => !this.isSelected(item));
        const toBeDeselected = itemsUniq.filter(item => this.isSelected(item));
        return this._queue(() => this._modify(uniqArray<T>(toBeSelected, this._idField), uniqArray<T>(toBeDeselected, this._idField)));
    }
}

export class SingleSelection<T> extends AbstractSelection<T> {
    // @toDo: change from public to protected
    //	public _idField: string;
    public _selected: number;
    //	public _size: number;

    private lastSelectedItem: T = null;

    constructor(selected: number, idField: string) {
        super();
        this._selected = selected;
        this._idField = idField;
        this._size = this._selected > 0 ? 1 : 0;
    }

    public toggle(item: T) {
        if (this.isSelected(item)) {
            return this.select([item]);
        }
        this.setSelection(item, !this.isSelected(item));
        if (this.isSelected(item)) {
            return this.select([item]);
        } else {
            return this.deselect([item]);
        }
    }

    public update() {
        return this.select([]);
    }

    public isSelected(item: T): boolean {
        return (item && item[this._idField] === this._selected) || false;
    }

    public setSelection(item: T, value: boolean): void {
        if (value && !this.isSelected(item)) {
            this._selected = (item && item[this._idField]) || 0;
            this.lastSelectedItem = item;
        } else if (!value && this.isSelected(item)) {
            this._selected = 0;
            this.lastSelectedItem = null;
        }
        this.emit('updated', this._selected);
        this._updateState(this._selected, 1);
    }

    public async getSelectedItems() {
        return Promise.resolve([this._selected]);
    }

    public get single(): boolean {
        return true;
    }

    public get global() {
        return false;
    }

    public get lastSelected(): T {
        return this.lastSelectedItem;
    }

    protected _modify(_selectItems: T[], _deselectItems: T[]): Promise<void> {
        this._size = this._selected > 0 ? 1 : 0;
        return Promise.resolve();
    }

    protected _selectAll(): Promise<void> {
        return Promise.resolve();
    }

    protected _deselectAll(): Promise<void> {
        return this._remove();
    }

    protected _invertAll(): Promise<void> {
        return Promise.resolve();
    }

    protected _remove(): Promise<void> {
        this._selected = 0;
        this.emit('updated', this._selected);
        this._updateState(0, 0);
        return Promise.resolve();
    }
}
